import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  ICreateFoodDietaryTagRequest,
  IUpdateFoodDietaryTagRequest,
  IFoodDietaryTagResponse,
  ICreateFoodKitchenRequest,
  IUpdateFoodKitchenRequest,
  IFoodKitchenResponse,
  ICreateFoodMealWindowRequest,
  IUpdateFoodMealWindowRequest,
  IFoodMealWindowResponse,
  ICreateFoodCatchmentRuleRequest,
  IUpdateFoodCatchmentRuleRequest,
  IFoodCatchmentRuleResponse,
  IEmployeeFoodProfile,
  IUpsertEmployeeFoodProfileRequest,
  IFoodConfigVersion,
  IUpsertFoodSitePolicyRequest,
  INextDayPreviewRow,
  IFoodDashboardResponse,
  IUpsertFoodStandbyConfigRequest,
  IFoodStandbyConfigResponse,
  ICreateKitchenDishRequest,
  IUpdateKitchenDishRequest,
  IKitchenDishResponse,
  ICreateKitchenIngredientRequest,
  IUpdateKitchenIngredientRequest,
  IKitchenIngredientResponse,
  ICreateKitchenNormativeVersionRequest,
  IKitchenNormativeVersionResponse,
  IFoodExceptionRecordResponse,
  IResolveFoodExceptionRequest,
  IFoodMealRequestResponse,
  ICreateFoodMealRequestRequest,
  IDecideFoodMealRequestRequest,
  IFoodHistoryDayResponse,
  IFoodWindowSummaryResponse,
  IFoodEmployeeForWindowResponse,
  IConfirmFoodKitchenRequest,
  IFoodKitchenConfirmationResponse,
  ICreateFoodManualAdjustmentRequest,
  IFoodManualAdjustmentResponse,
  IBulkCancelFoodRecordsRequest,
  IUpsertKitchenMenuPlanRequest,
  IKitchenMenuPlanResponse,
  IUpsertKitchenMenuCycleTemplateRequest,
  IKitchenMenuCycleTemplateResponse,
  IInstantiateKitchenMenuTemplateRequest,
  IKitchenTemplateInstantiationResponse,
  IKitchenFeasibilityResponse,
  IKitchenProductionWindowResponse,
  IKitchenSuggestionResponse,
  IKitchenVarianceResponse,
  IKitchenWasteResponse,
  IKitchenContractorOrderResponse,
} from 'types/api/foodApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IFoodSlice } from 'types/food.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, handleErrors } from 'utils/helpers'

const initialState: IFoodSlice = {
  isLoading: false,
  dishes: {
    data: [],
    singleDish: null,
    totalPages: 0,
    totalCount: 0,
  },
  kitchenIngredients: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  dietaryTags: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  logisticsKitchens: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  mealWindows: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  catchmentRules: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  employeeFoodProfiles: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  selectedEmployeeFoodProfile: null,
  configVersions: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  nextDayPreview: [],
  dashboard: null,
  kitchenStandbyConfigs: {},
  exceptions: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  mealRequests: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  historyDays: [],
  selectedWindow: null,
  windowEmployees: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  windowAdjustments: [],
  menuPlans: [],
  menuTemplates: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  kitchenProduction: [],
  kitchenSuggestions: [],
  kitchenVariance: [],
  kitchenWaste: [],
  menuFeasibility: null,
  contractorOrders: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
}

export const getFoodDietaryTags = createAsyncThunk(
  'food/getFoodDietaryTags',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodDietaryTagResponse>> = await baseUrl.get(
        'food/management/dietary-tag',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodDietaryTag = createAsyncThunk(
  'food/createFoodDietaryTag',
  async (body: ICreateFoodDietaryTagRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodDietaryTagResponse> = await baseUrl.post(
        'food/management/dietary-tag',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFoodDietaryTag = createAsyncThunk(
  'food/updateFoodDietaryTag',
  async ({ code, body }: { code: string; body: IUpdateFoodDietaryTagRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodDietaryTagResponse> = await baseUrl.patch(
        'food/management/dietary-tag/' + code,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const archiveFoodDietaryTag = createAsyncThunk(
  'food/archiveFoodDietaryTag',
  async (code: string, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/dietary-tag/' + code)
      return code
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodKitchens = createAsyncThunk(
  'food/getFoodKitchens',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodKitchenResponse>> = await baseUrl.get(
        'food/management/kitchen',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodKitchen = createAsyncThunk(
  'food/createFoodKitchen',
  async (body: ICreateFoodKitchenRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodKitchenResponse> = await baseUrl.post(
        'food/management/kitchen',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFoodKitchen = createAsyncThunk(
  'food/updateFoodKitchen',
  async ({ id, body }: { id: number; body: IUpdateFoodKitchenRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodKitchenResponse> = await baseUrl.patch(
        'food/management/kitchen/' + id,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const archiveFoodKitchen = createAsyncThunk(
  'food/archiveFoodKitchen',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/kitchen/' + id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodKitchenStandbyConfigs = createAsyncThunk(
  'food/getFoodKitchenStandbyConfigs',
  async (kitchenId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodStandbyConfigResponse[]> = await baseUrl.get(
        `food/management/kitchen/${kitchenId}/standby`,
      )
      return { kitchenId, configs: resp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const upsertFoodKitchenStandbyConfig = createAsyncThunk(
  'food/upsertFoodKitchenStandbyConfig',
  async (
    { kitchenId, body }: { kitchenId: number; body: IUpsertFoodStandbyConfigRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IFoodStandbyConfigResponse> = await baseUrl.put(
        `food/management/kitchen/${kitchenId}/standby`,
        body,
      )
      await thunkAPI.dispatch(getFoodKitchenStandbyConfigs(kitchenId))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodKitchenStandbyConfig = createAsyncThunk(
  'food/deleteFoodKitchenStandbyConfig',
  async ({ kitchenId, standbyId }: { kitchenId: number; standbyId: number }, thunkAPI) => {
    try {
      await baseUrl.delete(`food/management/kitchen/${kitchenId}/standby/${standbyId}`)
      return { kitchenId, standbyId }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodMealWindows = createAsyncThunk(
  'food/getFoodMealWindows',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodMealWindowResponse>> = await baseUrl.get(
        'food/management/meal-window',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodMealWindow = createAsyncThunk(
  'food/createFoodMealWindow',
  async (body: ICreateFoodMealWindowRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodMealWindowResponse> = await baseUrl.post(
        'food/management/meal-window',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFoodMealWindow = createAsyncThunk(
  'food/updateFoodMealWindow',
  async ({ id, body }: { id: number; body: IUpdateFoodMealWindowRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodMealWindowResponse> = await baseUrl.patch(
        'food/management/meal-window/' + id,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodMealWindow = createAsyncThunk(
  'food/deleteFoodMealWindow',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/meal-window/' + id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodCatchmentRules = createAsyncThunk(
  'food/getFoodCatchmentRules',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodCatchmentRuleResponse>> = await baseUrl.get(
        'food/management/catchment-rule',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodCatchmentRule = createAsyncThunk(
  'food/createFoodCatchmentRule',
  async (body: ICreateFoodCatchmentRuleRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodCatchmentRuleResponse> = await baseUrl.post(
        'food/management/catchment-rule',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFoodCatchmentRule = createAsyncThunk(
  'food/updateFoodCatchmentRule',
  async ({ id, body }: { id: number; body: IUpdateFoodCatchmentRuleRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodCatchmentRuleResponse> = await baseUrl.patch(
        'food/management/catchment-rule/' + id,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodCatchmentRule = createAsyncThunk(
  'food/deleteFoodCatchmentRule',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/catchment-rule/' + id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodEmployeeProfiles = createAsyncThunk(
  'food/getFoodEmployeeProfiles',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IEmployeeFoodProfile>> = await baseUrl.get(
        'food/management/profile',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodEmployeeProfile = createAsyncThunk(
  'food/getFoodEmployeeProfile',
  async (userId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IEmployeeFoodProfile> = await baseUrl.get(
        'food/management/profile/' + userId,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const upsertFoodEmployeeProfile = createAsyncThunk(
  'food/upsertFoodEmployeeProfile',
  async (
    { userId, body }: { userId: number; body: IUpsertEmployeeFoodProfileRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IEmployeeFoodProfile> = await baseUrl.put(
        'food/management/profile/' + userId,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodConfigVersions = createAsyncThunk(
  'food/getFoodConfigVersions',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodConfigVersion>> = await baseUrl.get(
        'food/management/config/versions',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const upsertFoodSitePolicy = createAsyncThunk(
  'food/upsertFoodSitePolicy',
  async (body: IUpsertFoodSitePolicyRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodConfigVersion> = await baseUrl.put(
        'food/management/config/site-policy',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodIntentsPreview = createAsyncThunk(
  'food/getFoodIntentsPreview',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<INextDayPreviewRow[]> = await baseUrl.get(
        'food/management/intents/preview',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodDashboard = createAsyncThunk(
  'food/getFoodDashboard',
  async (day: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodDashboardResponse> = await baseUrl.get(
        'food/management/dashboard',
        { params: { day } },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const finalizeFoodDay = createAsyncThunk(
  'food/finalizeFoodDay',
  async (day: string, thunkAPI) => {
    try {
      const resp = await baseUrl.post('food/management/days/finalize', null, { params: { day } })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const finalizeFoodWindow = createAsyncThunk(
  'food/finalizeFoodWindow',
  async (runId: number, thunkAPI) => {
    try {
      const resp = await baseUrl.post(`food/management/windows/${runId}/finalize`)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodHistory = createAsyncThunk(
  'food/getFoodHistory',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodHistoryDayResponse[]> = await baseUrl.get(
        'food/management/history',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodWindow = createAsyncThunk(
  'food/getFoodWindow',
  async (runId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodWindowSummaryResponse> = await baseUrl.get(
        `food/management/windows/${runId}`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodWindowEmployees = createAsyncThunk(
  'food/getFoodWindowEmployees',
  async ({ runId, params }: { runId: number; params: any }, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodEmployeeForWindowResponse>> =
        await baseUrl.get(`food/management/windows/${runId}/employees`, { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const confirmFoodKitchen = createAsyncThunk(
  'food/confirmFoodKitchen',
  async (
    {
      runId,
      kitchenId,
      body,
    }: { runId: number; kitchenId: number; body: IConfirmFoodKitchenRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IFoodKitchenConfirmationResponse> = await baseUrl.post(
        `food/management/windows/${runId}/kitchens/${kitchenId}/confirmation`,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodWindowAdjustment = createAsyncThunk(
  'food/createFoodWindowAdjustment',
  async (
    { runId, body }: { runId: number; body: ICreateFoodManualAdjustmentRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IFoodManualAdjustmentResponse> = await baseUrl.post(
        `food/management/windows/${runId}/adjustments`,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodWindowAdjustments = createAsyncThunk(
  'food/getFoodWindowAdjustments',
  async (runId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodManualAdjustmentResponse[]> = await baseUrl.get(
        `food/management/windows/${runId}/adjustments`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const bulkCancelFoodRecords = createAsyncThunk(
  'food/bulkCancelFoodRecords',
  async (body: IBulkCancelFoodRecordsRequest, thunkAPI) => {
    try {
      const resp = await baseUrl.post('food/management/records/bulk/cancel', body)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDishes = createAsyncThunk('food/getAllDishes', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IKitchenDishResponse>> = await baseUrl.get(
      'kitchen/management/dish',
      { params },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getSingleDish = createAsyncThunk(
  'food/getSingleDish',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenDishResponse> = await baseUrl.get(
        'kitchen/management/dish/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createDish = createAsyncThunk(
  'food/createDish',
  async (body: ICreateKitchenDishRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenDishResponse> = await baseUrl.post(
        'kitchen/management/dish',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editDish = createAsyncThunk(
  'food/editDish',
  async ({ id, body }: { id: number; body: IUpdateKitchenDishRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenDishResponse> = await baseUrl.patch(
        'kitchen/management/dish/' + id,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createDishNormativeVersion = createAsyncThunk(
  'food/createDishNormativeVersion',
  async (
    { dishId, body }: { dishId: number; body: ICreateKitchenNormativeVersionRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IKitchenNormativeVersionResponse> = await baseUrl.post(
        `kitchen/management/dish/${dishId}/normative`,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenIngredients = createAsyncThunk(
  'food/getKitchenIngredients',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IKitchenIngredientResponse>> = await baseUrl.get(
        'kitchen/management/ingredient',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createKitchenIngredient = createAsyncThunk(
  'food/createKitchenIngredient',
  async (body: ICreateKitchenIngredientRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenIngredientResponse> = await baseUrl.post(
        'kitchen/management/ingredient',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editKitchenIngredient = createAsyncThunk(
  'food/editKitchenIngredient',
  async ({ id, body }: { id: number; body: IUpdateKitchenIngredientRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenIngredientResponse> = await baseUrl.patch(
        'kitchen/management/ingredient/' + id,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodExceptions = createAsyncThunk(
  'food/getFoodExceptions',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodExceptionRecordResponse>> =
        await baseUrl.get('food/management/exception', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const resolveFoodException = createAsyncThunk(
  'food/resolveFoodException',
  async ({ id, body }: { id: number; body: IResolveFoodExceptionRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodExceptionRecordResponse> = await baseUrl.post(
        `food/management/exception/${id}/resolve`,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodMealRequests = createAsyncThunk(
  'food/getFoodMealRequests',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodMealRequestResponse>> = await baseUrl.get(
        'food/management/request',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodMealRequest = createAsyncThunk(
  'food/createFoodMealRequest',
  async (body: ICreateFoodMealRequestRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodMealRequestResponse> = await baseUrl.post(
        'food/management/request',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const decideFoodMealRequest = createAsyncThunk(
  'food/decideFoodMealRequest',
  async ({ id, body }: { id: number; body: IDecideFoodMealRequestRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodMealRequestResponse> = await baseUrl.post(
        `food/management/request/${id}/decide`,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const archiveKitchenIngredient = createAsyncThunk(
  'food/archiveKitchenIngredient',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`kitchen/management/ingredient/${id}/archive`)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenMenuPlans = createAsyncThunk(
  'food/getKitchenMenuPlans',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenMenuPlanResponse[]> = await baseUrl.get(
        'kitchen/management/menu/plan',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const upsertKitchenMenuPlan = createAsyncThunk(
  'food/upsertKitchenMenuPlan',
  async (body: IUpsertKitchenMenuPlanRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenMenuPlanResponse> = await baseUrl.put(
        'kitchen/management/menu/plan',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const publishKitchenMenuPlan = createAsyncThunk(
  'food/publishKitchenMenuPlan',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenMenuPlanResponse> = await baseUrl.post(
        `kitchen/management/menu/plan/${id}/publish`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenMenuFeasibility = createAsyncThunk(
  'food/getKitchenMenuFeasibility',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenFeasibilityResponse> = await baseUrl.get(
        `kitchen/management/menu/plan/${id}/feasibility`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenMenuTemplates = createAsyncThunk(
  'food/getKitchenMenuTemplates',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IKitchenMenuCycleTemplateResponse>> =
        await baseUrl.get('kitchen/management/menu/template', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const upsertKitchenMenuTemplate = createAsyncThunk(
  'food/upsertKitchenMenuTemplate',
  async (body: IUpsertKitchenMenuCycleTemplateRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenMenuCycleTemplateResponse> = await baseUrl.put(
        'kitchen/management/menu/template',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const instantiateKitchenMenuTemplate = createAsyncThunk(
  'food/instantiateKitchenMenuTemplate',
  async (
    { id, body }: { id: number; body: IInstantiateKitchenMenuTemplateRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IKitchenTemplateInstantiationResponse> = await baseUrl.post(
        `kitchen/management/menu/template/${id}/instantiate`,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenProduction = createAsyncThunk(
  'food/getKitchenProduction',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenProductionWindowResponse[]> = await baseUrl.get(
        'kitchen/management/production',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenSuggestions = createAsyncThunk(
  'food/getKitchenSuggestions',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenSuggestionResponse[]> = await baseUrl.get(
        'kitchen/management/suggestion',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenVariance = createAsyncThunk(
  'food/getKitchenVariance',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenVarianceResponse[]> = await baseUrl.get(
        'kitchen/management/variance',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenWaste = createAsyncThunk(
  'food/getKitchenWaste',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IKitchenWasteResponse[]> = await baseUrl.get(
        'kitchen/management/waste',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getKitchenContractorOrders = createAsyncThunk(
  'food/getKitchenContractorOrders',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IKitchenContractorOrderResponse>> =
        await baseUrl.get('kitchen/management/contractor/order', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const retryKitchenConsumption = createAsyncThunk(
  'food/retryKitchenConsumption',
  async (mealWindowRunId: number, thunkAPI) => {
    try {
      const resp = await baseUrl.post(
        `kitchen/management/windows/${mealWindowRunId}/consumption/retry`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const foodSlice = createSlice({
  name: 'food',
  initialState,
  reducers: {
    clearSingleDish: (state) => {
      state.dishes.singleDish = null
    },
    clearFood: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state = initialState
    },
    clearSelectedEmployeeFoodProfile: (state) => {
      state.selectedEmployeeFoodProfile = null
    },
    clearFoodIntentsPreview: (state) => {
      state.nextDayPreview = []
    },
    clearFoodDashboard: (state) => {
      state.dashboard = null
    },
    clearSelectedFoodWindow: (state) => {
      state.selectedWindow = null
      state.windowEmployees = { data: [], totalPages: 0, totalCount: 0 }
      state.windowAdjustments = []
    },
  },
  extraReducers: {
    [getFoodDietaryTags.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodDietaryTags.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodDietaryTagResponse> },
    ) => {
      state.isLoading = false
      state.dietaryTags = payload
    },
    [getFoodDietaryTags.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodDietaryTag.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodDietaryTagResponse },
    ) => {
      state.dietaryTags.data = [payload, ...state.dietaryTags.data]
      toast.success('Dietary tag created successfully')
    },
    [createFoodDietaryTag.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFoodDietaryTag.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodDietaryTagResponse },
    ) => {
      state.dietaryTags.data = state.dietaryTags.data.map((row) =>
        row.code === payload.code ? payload : row,
      )
      toast.success('Dietary tag updated successfully')
    },
    [updateFoodDietaryTag.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [archiveFoodDietaryTag.fulfilled.type]: (state, { payload }: { payload: string }) => {
      state.dietaryTags.data = state.dietaryTags.data.filter((row) => row.code !== payload)
      toast.success('Dietary tag archived successfully')
    },
    [archiveFoodDietaryTag.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodKitchens.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodKitchens.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodKitchenResponse> },
    ) => {
      state.isLoading = false
      state.logisticsKitchens = payload
    },
    [getFoodKitchens.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodKitchen.fulfilled.type]: (state, { payload }: { payload: IFoodKitchenResponse }) => {
      state.logisticsKitchens.data = [payload, ...state.logisticsKitchens.data]
      toast.success('Kitchen created successfully')
    },
    [createFoodKitchen.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFoodKitchen.fulfilled.type]: (state, { payload }: { payload: IFoodKitchenResponse }) => {
      state.logisticsKitchens.data = state.logisticsKitchens.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Kitchen updated successfully')
    },
    [updateFoodKitchen.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [archiveFoodKitchen.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.logisticsKitchens.data = state.logisticsKitchens.data.filter(
        (row) => row.id !== payload,
      )
      toast.success('Kitchen archived successfully')
    },
    [archiveFoodKitchen.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodKitchenStandbyConfigs.fulfilled.type]: (
      state,
      { payload }: { payload: { kitchenId: number; configs: IFoodStandbyConfigResponse[] } },
    ) => {
      state.kitchenStandbyConfigs[payload.kitchenId] = payload.configs
    },
    [getFoodKitchenStandbyConfigs.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [upsertFoodKitchenStandbyConfig.fulfilled.type]: () => {
      toast.success('Standby configuration saved successfully')
    },
    [upsertFoodKitchenStandbyConfig.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [deleteFoodKitchenStandbyConfig.fulfilled.type]: (
      state,
      { payload }: { payload: { kitchenId: number; standbyId: number } },
    ) => {
      state.kitchenStandbyConfigs[payload.kitchenId] = (
        state.kitchenStandbyConfigs[payload.kitchenId] || []
      ).filter((row) => row.id !== payload.standbyId)
      toast.success('Standby configuration removed successfully')
    },
    [deleteFoodKitchenStandbyConfig.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodMealWindows.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodMealWindows.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodMealWindowResponse> },
    ) => {
      state.isLoading = false
      state.mealWindows = payload
    },
    [getFoodMealWindows.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodMealWindow.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodMealWindowResponse },
    ) => {
      state.mealWindows.data = [payload, ...state.mealWindows.data]
      toast.success('Meal window created successfully')
    },
    [createFoodMealWindow.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFoodMealWindow.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodMealWindowResponse },
    ) => {
      state.mealWindows.data = state.mealWindows.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Meal window updated successfully')
    },
    [updateFoodMealWindow.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [deleteFoodMealWindow.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.mealWindows.data = state.mealWindows.data.filter((row) => row.id !== payload)
      toast.success('Meal window deleted successfully')
    },
    [deleteFoodMealWindow.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodCatchmentRules.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodCatchmentRules.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodCatchmentRuleResponse> },
    ) => {
      state.isLoading = false
      state.catchmentRules = payload
    },
    [getFoodCatchmentRules.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodCatchmentRule.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodCatchmentRuleResponse },
    ) => {
      state.catchmentRules.data = [payload, ...state.catchmentRules.data]
      toast.success('Catchment rule created successfully')
    },
    [createFoodCatchmentRule.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFoodCatchmentRule.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodCatchmentRuleResponse },
    ) => {
      state.catchmentRules.data = state.catchmentRules.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Catchment rule updated successfully')
    },
    [updateFoodCatchmentRule.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [deleteFoodCatchmentRule.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.catchmentRules.data = state.catchmentRules.data.filter((row) => row.id !== payload)
      toast.success('Catchment rule deleted successfully')
    },
    [deleteFoodCatchmentRule.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodEmployeeProfiles.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IEmployeeFoodProfile> },
    ) => {
      state.employeeFoodProfiles = payload
    },
    [getFoodEmployeeProfiles.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodEmployeeProfile.fulfilled.type]: (
      state,
      { payload }: { payload: IEmployeeFoodProfile },
    ) => {
      state.selectedEmployeeFoodProfile = payload
    },
    [getFoodEmployeeProfile.rejected.type]: (state, { payload }) => {
      state.selectedEmployeeFoodProfile = null
      handleErrors(payload)
    },
    [upsertFoodEmployeeProfile.fulfilled.type]: (
      state,
      { payload }: { payload: IEmployeeFoodProfile },
    ) => {
      state.selectedEmployeeFoodProfile = payload
      const userId = payload.user?.id
      const exists = state.employeeFoodProfiles.data.some((row) => row.user?.id === userId)
      state.employeeFoodProfiles.data = exists
        ? state.employeeFoodProfiles.data.map((row) =>
            row.user?.id === userId ? payload : row,
          )
        : [payload, ...state.employeeFoodProfiles.data]
      toast.success('Food profile saved')
    },
    [upsertFoodEmployeeProfile.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodConfigVersions.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodConfigVersion> },
    ) => {
      state.configVersions = payload
    },
    [getFoodConfigVersions.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [upsertFoodSitePolicy.fulfilled.type]: (state, { payload }: { payload: IFoodConfigVersion }) => {
      if (payload?.id) {
        state.configVersions.data = [payload, ...(state.configVersions.data || [])]
        toast.success('Site policy saved — effective next operational day')
      } else {
        toast.info('Site policy unchanged — no new config version created')
      }
    },
    [upsertFoodSitePolicy.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodIntentsPreview.fulfilled.type]: (
      state,
      { payload }: { payload: INextDayPreviewRow[] },
    ) => {
      state.nextDayPreview = payload || []
    },
    [getFoodIntentsPreview.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodDashboard.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodDashboard.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodDashboardResponse },
    ) => {
      state.isLoading = false
      state.dashboard = payload
    },
    [getFoodDashboard.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [finalizeFoodDay.fulfilled.type]: () => {
      toast.success('Operational day finalized successfully')
    },
    [finalizeFoodDay.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [finalizeFoodWindow.fulfilled.type]: () => {
      toast.success('Meal window finalized successfully')
    },
    [finalizeFoodWindow.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getDishes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDishes.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IKitchenDishResponse> },
    ) => {
      state.isLoading = false
      state.dishes.data = payload.data
      state.dishes.totalCount = payload.totalCount
      state.dishes.totalPages = payload.totalPages
    },
    [getDishes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleDish.fulfilled.type]: (state, { payload }: { payload: IKitchenDishResponse }) => {
      state.isLoading = false
      state.dishes.singleDish = payload
    },
    [getSingleDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [createDish.fulfilled.type]: (state, { payload }: { payload: IKitchenDishResponse }) => {
      state.isLoading = false
      state.dishes.data = [payload, ...state.dishes.data]
      toast.success('Dish created successfully')
    },
    [createDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [editDish.fulfilled.type]: (state, { payload }: { payload: IKitchenDishResponse }) => {
      state.isLoading = false
      state.dishes.data = state.dishes.data.map((row) => (row.id === payload.id ? payload : row))
      toast.success('Dish updated successfully')
    },
    [editDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createDishNormativeVersion.fulfilled.type]: () => {
      toast.success('Recipe version saved successfully')
    },
    [createDishNormativeVersion.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getKitchenIngredients.pending.type]: (state) => {
      state.isLoading = true
    },
    [getKitchenIngredients.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IKitchenIngredientResponse> },
    ) => {
      state.isLoading = false
      state.kitchenIngredients = payload
    },
    [getKitchenIngredients.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createKitchenIngredient.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenIngredientResponse },
    ) => {
      state.kitchenIngredients.data = [payload, ...state.kitchenIngredients.data]
      toast.success('Ingredient created successfully')
    },
    [createKitchenIngredient.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [editKitchenIngredient.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenIngredientResponse },
    ) => {
      state.kitchenIngredients.data = state.kitchenIngredients.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Ingredient updated successfully')
    },
    [editKitchenIngredient.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [archiveKitchenIngredient.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.kitchenIngredients.data = state.kitchenIngredients.data.filter(
        (row) => row.id !== payload,
      )
      toast.success('Ingredient archived successfully')
    },
    [archiveKitchenIngredient.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodExceptions.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodExceptions.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodExceptionRecordResponse> },
    ) => {
      state.isLoading = false
      state.exceptions = payload
    },
    [getFoodExceptions.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [resolveFoodException.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodExceptionRecordResponse },
    ) => {
      state.exceptions.data = state.exceptions.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Exception resolved successfully')
    },
    [resolveFoodException.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodMealRequests.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodMealRequests.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodMealRequestResponse> },
    ) => {
      state.isLoading = false
      state.mealRequests = payload
    },
    [getFoodMealRequests.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodMealRequest.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodMealRequestResponse },
    ) => {
      state.mealRequests.data = [payload, ...state.mealRequests.data]
      toast.success('Meal request submitted successfully')
    },
    [createFoodMealRequest.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [decideFoodMealRequest.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodMealRequestResponse },
    ) => {
      state.mealRequests.data = state.mealRequests.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Meal request decided successfully')
    },
    [decideFoodMealRequest.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodHistory.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodHistory.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodHistoryDayResponse[] },
    ) => {
      state.isLoading = false
      state.historyDays = payload || []
    },
    [getFoodHistory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodWindow.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodWindow.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodWindowSummaryResponse },
    ) => {
      state.isLoading = false
      state.selectedWindow = payload
    },
    [getFoodWindow.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodWindowEmployees.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodWindowEmployees.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodEmployeeForWindowResponse> },
    ) => {
      state.isLoading = false
      state.windowEmployees = {
        data: payload.data || [],
        totalPages: payload.totalPages,
        totalCount: payload.totalCount,
      }
    },
    [getFoodWindowEmployees.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [confirmFoodKitchen.fulfilled.type]: () => {
      toast.success('Kitchen confirmation saved')
    },
    [confirmFoodKitchen.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [createFoodWindowAdjustment.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodManualAdjustmentResponse },
    ) => {
      state.windowAdjustments = [payload, ...state.windowAdjustments]
      toast.success('Plan adjustment submitted')
    },
    [createFoodWindowAdjustment.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodWindowAdjustments.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodManualAdjustmentResponse[] },
    ) => {
      state.windowAdjustments = payload || []
    },
    [getFoodWindowAdjustments.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [bulkCancelFoodRecords.fulfilled.type]: (state, { payload }) => {
      toast.success(payload?.message || 'Meal(s) cancelled')
    },
    [bulkCancelFoodRecords.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getKitchenMenuPlans.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenMenuPlanResponse[] },
    ) => {
      state.menuPlans = payload || []
    },
    [getKitchenMenuPlans.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [upsertKitchenMenuPlan.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenMenuPlanResponse },
    ) => {
      const exists = state.menuPlans.some((p) => p.id === payload.id)
      state.menuPlans = exists
        ? state.menuPlans.map((p) => (p.id === payload.id ? payload : p))
        : [payload, ...state.menuPlans]
      toast.success('Menu plan saved')
    },
    [upsertKitchenMenuPlan.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [publishKitchenMenuPlan.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenMenuPlanResponse },
    ) => {
      state.menuPlans = state.menuPlans.map((p) => (p.id === payload.id ? payload : p))
      toast.success('Menu plan published')
    },
    [publishKitchenMenuPlan.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getKitchenMenuFeasibility.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenFeasibilityResponse },
    ) => {
      state.menuFeasibility = payload
    },
    [getKitchenMenuFeasibility.rejected.type]: (state, { payload }) => {
      state.menuFeasibility = null
      handleErrors(payload)
    },
    [getKitchenMenuTemplates.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IKitchenMenuCycleTemplateResponse> },
    ) => {
      state.menuTemplates = {
        data: payload.data || [],
        totalPages: payload.totalPages,
        totalCount: payload.totalCount,
      }
    },
    [getKitchenMenuTemplates.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [upsertKitchenMenuTemplate.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenMenuCycleTemplateResponse },
    ) => {
      const exists = state.menuTemplates.data.some((t) => t.id === payload.id)
      state.menuTemplates.data = exists
        ? state.menuTemplates.data.map((t) => (t.id === payload.id ? payload : t))
        : [payload, ...state.menuTemplates.data]
      toast.success('Menu template saved')
    },
    [upsertKitchenMenuTemplate.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [instantiateKitchenMenuTemplate.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenTemplateInstantiationResponse },
    ) => {
      toast.success(
        payload.dryRun
          ? `Dry run: ${payload.filled} fill(s), ${payload.skipped} skipped`
          : `Template applied: ${payload.filled} fill(s), ${payload.skipped} skipped`,
      )
    },
    [instantiateKitchenMenuTemplate.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getKitchenProduction.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenProductionWindowResponse[] },
    ) => {
      state.kitchenProduction = payload || []
    },
    [getKitchenProduction.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getKitchenSuggestions.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenSuggestionResponse[] },
    ) => {
      state.kitchenSuggestions = payload || []
    },
    [getKitchenSuggestions.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getKitchenVariance.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenVarianceResponse[] },
    ) => {
      state.kitchenVariance = payload || []
    },
    [getKitchenVariance.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getKitchenWaste.fulfilled.type]: (
      state,
      { payload }: { payload: IKitchenWasteResponse[] },
    ) => {
      state.kitchenWaste = payload || []
    },
    [getKitchenWaste.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getKitchenContractorOrders.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IKitchenContractorOrderResponse> },
    ) => {
      state.contractorOrders = {
        data: payload.data || [],
        totalPages: payload.totalPages,
        totalCount: payload.totalCount,
      }
    },
    [getKitchenContractorOrders.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [retryKitchenConsumption.fulfilled.type]: (state, { payload }) => {
      toast.success(payload?.message || 'Consumption posting queued')
    },
    [retryKitchenConsumption.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
  },
})

export const {
  clearFood,
  clearSingleDish,
  clearSelectedEmployeeFoodProfile,
  clearFoodIntentsPreview,
  clearFoodDashboard,
  clearSelectedFoodWindow,
} = foodSlice.actions
export default foodSlice.reducer
